import { useState, useEffect } from "react";
import { TfiLayoutLineSolid } from "react-icons/tfi";
import {
  FiX,
  FiChevronDown,
  FiCheckCircle,
  FiRefreshCcw,
} from "react-icons/fi";
import "./Filter.css";
import { useBasicContext } from "../context/Basic_context";

const DEFAULT_FILTERS = {
  ageMin: "",
  ageMax: "",
  caste: "",
  maritalStatus: "",
  country: "",
  state: "",
  city: "",
};

const DEFAULT_LABELS = {
  caste: "",
  maritalStatus: "",
  country: "",
  state: "",
  city: "",
};

const SelectField = ({
  label,
  value,
  onChange,
  options = [],
  placeholder,
  errorMsg,
  loading,
}) => (
  <div className="fl-group">
    <label className="fl-label">{label}</label>
    <div className="fl-select-wrap">
      <select
        className="fl-select"
        value={value}
        onChange={(e) => {
          const selectedOpt = options.find(
            (o) => String(o.value) === String(e.target.value),
          );
          onChange(e.target.value, selectedOpt?.label ?? "");
        }}
        disabled={loading}
      >
        <option value="">
          {loading
            ? "Loading..."
            : options.length === 0 && errorMsg
              ? errorMsg
              : placeholder}
        </option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <FiChevronDown className="fl-select-arrow" size={16} />
    </div>
  </div>
);

const Filter = ({ isOpen, onClose, onApply, onReset }) => {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [labels, setLabels] = useState(DEFAULT_LABELS);

  const {
    cast_loading,
    cast_data,
    coutry_loading,
    country_data,
    state_loading,
    state_data,
    city_loading,
    city_data,
    filterMaritialstatus_loading,
    filterMaritialstatus_data,
    GetCasteApi,
    GetCountryApi,
    GetStateApi,
    GetCityApi,
    GetMaritalStatusDashFilterApi,
  } = useBasicContext();

  useEffect(() => {
    GetCasteApi();
    GetCountryApi();
    GetMaritalStatusDashFilterApi();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      window.history.pushState({ filterOpen: true }, "");
      const handleBackButton = () => onClose?.();
      window.addEventListener("popstate", handleBackButton);
      return () => {
        window.removeEventListener("popstate", handleBackButton);
        document.body.style.overflow = "";
      };
    }
  }, [isOpen, onClose]);

  const handleManualClose = () => {
    onClose?.();
  };
  const casteOptions = cast_data.map((c) => ({
    value: c.id ?? c,
    label: c.name ?? c.title ?? String(c),
  }));
  const maritalOptions = filterMaritialstatus_data.map((m) => ({
    value: m.id ?? m,
    label: m.name ?? m.title ?? String(m),
  }));
  const countryOptions = country_data.map((c) => ({
    value: c.id ?? c,
    label: c.name ?? c.title ?? String(c),
  }));
  const stateOptions = state_data.map((s) => ({
    value: s.id ?? s,
    label: s.name ?? s.title ?? String(s),
  }));
  const cityOptions = city_data.map((c) => ({
    value: c.id ?? c,
    label: c.name ?? c.title ?? String(c),
  }));

  const set =
    (key) =>
    (val, labelVal = "") => {
      setFilters((prev) => {
        const next = { ...prev, [key]: val };
        if (key === "country") {
          next.state = "";
          next.city = "";
        }
        if (key === "state") {
          next.city = "";
        }
        return next;
      });
      setLabels((prev) => {
        const next = { ...prev, [key]: labelVal };
        if (key === "country") {
          next.state = "";
          next.city = "";
        }
        if (key === "state") {
          next.city = "";
        }
        return next;
      });
      if (key === "country" && val) GetStateApi({ country_id: val });
      if (key === "state" && val) GetCityApi({ state_id: val });
    };

  const handleReset = () => {
    setFilters(DEFAULT_FILTERS);
    setLabels(DEFAULT_LABELS);
    onReset?.();
    handleManualClose();
  };

  const handleApply = () => {
    onApply?.({ ...filters }, { ...labels });
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <div className="fl-overlay" onClick={handleManualClose}>
      <div className="fl-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="fl-header">
          <div>
            <div className="fl-title">Filter Profiles</div>
            <div className="fl-subtitle">Find your perfect match</div>
          </div>
          <button className="fl-close-btn" onClick={handleManualClose}>
            <FiX size={17} />
          </button>
        </div>

        <div className="fl-body">
          <div>
            <label className="fl-label">Age Range</label>
            <div className="fl-dash">
              <div className="ageInputContainer">
                <label className="ageInputLabel">Min</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="ageInput"
                  placeholder="18"
                  value={filters.ageMin}
                  onChange={(e) => {
                    let val = e.target.value;

                    // only numbers allow
                    if (!/^\d*$/.test(val)) return;

                    // max 2 digits
                    if (val.length > 2) return;

                    set("ageMin")(val);
                  }}
                />
              </div>
              <span>
                <TfiLayoutLineSolid size={20} color="#ccc" />
              </span>
              <div className="ageInputContainer">
                <label className="ageInputLabel">Max</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className="ageInput"
                  placeholder="41+"
                  value={filters.ageMax}
                  onChange={(e) => {
                    let val = e.target.value;
                    if (!/^\d*$/.test(val)) return;

                    if (val.length > 2) return;

                    set("ageMax")(val);
                  }}
                />
              </div>
            </div>
            <span className="fl-tip">
              Tip: enter custom min/max age values.
            </span>
          </div>

          <SelectField
            label="Caste"
            value={filters.caste}
            onChange={set("caste")}
            options={casteOptions}
            placeholder="Select Caste"
            loading={cast_loading}
          />

          <SelectField
            label="Marital Status"
            value={filters.maritalStatus}
            onChange={set("maritalStatus")}
            options={maritalOptions}
            placeholder="Select Status"
            loading={filterMaritialstatus_loading}
          />

          <SelectField
            label="Country"
            value={filters.country}
            onChange={set("country")}
            options={countryOptions}
            placeholder="Select Country"
            loading={coutry_loading}
          />

          <SelectField
            label="State"
            value={filters.state}
            onChange={set("state")}
            options={stateOptions}
            placeholder="Select State"
            errorMsg={
              filters.country ? "No states found" : "Select Country first..."
            }
            loading={state_loading}
          />

          <SelectField
            label="City"
            value={filters.city}
            onChange={set("city")}
            options={cityOptions}
            placeholder="Select City"
            errorMsg={
              filters.state ? "No cities found" : "Select State first..."
            }
            loading={city_loading}
          />
        </div>

        <div className="fl-footer">
          <button className="fl-btn-reset" onClick={handleReset}>
            <FiRefreshCcw size={14} /> Reset
          </button>
          <button className="fl-btn-apply" onClick={handleApply}>
            Apply Filters <FiCheckCircle size={13} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Filter;
